var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AmbientLight, ArrowHelper, AxesHelper, Color, Euler, HalfFloatType, Matrix4, Mesh, MeshBasicMaterial, Object3D, PMREMGenerator, Scene, SphereGeometry, Vector3, WebGLRenderer, } from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { GlbConverter } from './modelPreviewService';
export class JointManagerView3D {
    constructor(canvas, cameraManager, environment, modelLoader, renderer) {
        this.canvas = canvas;
        this.cameraManager = cameraManager;
        this.environment = environment;
        this.modelLoader = modelLoader;
        this.renderer = renderer;
        this.debug = false;
        this.disableRerender = false;
        this.onJointPointChanged = () => { };
        this.resizeHandler = () => {
            this.updateSizes();
            this.cameraManager.resize(this.width, this.height);
            this.renderer.setSize(this.width, this.height);
        };
        this.handleTransformControlDragging = (event) => {
            this.cameraManager.enabled = !event.value;
        };
        this.handleTransformControlChange = () => {
            const position = this.jointPointNode.position.clone();
            const rotation = this.jointPointNode.rotation.clone();
            const jp = Object.assign(Object.assign({}, this.jointPointNode.userData), { jointPoint: {
                    position,
                    rotation,
                } });
            this.onJointPointChanged(jp);
        };
        this.width = canvas.offsetWidth;
        this.height = canvas.offsetHeight;
        const params = new URLSearchParams(location.search);
        this.debug = params.get('debug') !== null;
    }
    setConfiguration(configuration) {
        this.configuration = configuration;
    }
    getConfiguration() {
        return this.configuration;
    }
    setMode(mode) {
        this.control.setMode(mode);
    }
    jointPointChanged(callback) {
        this.onJointPointChanged = callback;
    }
    setActiveJointPoint(jointPoint) {
        const { name, jointPoint: { rotation }, } = jointPoint;
        if (jointPoint.jointPoint.direction) {
            const euler = this.getRotation({
                direction: jointPoint.jointPoint.direction,
                rotation: jointPoint.jointPoint.direction,
            });
            this.jointPointNode.rotation.copy(euler);
        }
        else {
            this.jointPointNode.rotation.set(rotation.x, rotation.y, rotation.z);
        }
        const dirArrow = new ArrowHelper(new Vector3(0, 1, 0), new Vector3(), 0.3, '#009688');
        const rotArrow = new ArrowHelper(new Vector3(1, 0, 0), new Vector3(), 0.3, '#af0606');
        const sphereGeometry = new SphereGeometry(0.01);
        const sphere = new Mesh(sphereGeometry, new MeshBasicMaterial({ color: 'red' }));
        this.jointPointNode.name = name;
        this.jointPointNode.userData = jointPoint;
        this.jointPointNode.add(rotArrow, dirArrow, sphere);
        this.jointPointNode.position.copy(jointPoint.jointPoint.position);
        this.control.enabled = true;
        this.control.getHelper().visible = true;
    }
    dispose() {
        if (this.renderer instanceof WebGLRenderer) {
            this.renderer.dispose();
            this.renderer = null;
            this.destroy();
        }
    }
    getRotation(params) {
        const { direction, rotation } = params;
        const dirVector = new Vector3().copy(direction).normalize();
        const rotVector = new Vector3().copy(rotation).normalize();
        const zz = new Vector3().crossVectors(rotVector.normalize(), dirVector.normalize());
        const m = new Matrix4().makeBasis(rotVector, dirVector, zz);
        const r = new Matrix4();
        r.extractRotation(m);
        return new Euler().setFromRotationMatrix(r, 'XYZ');
    }
    init(configuration) {
        return __awaiter(this, void 0, void 0, function* () {
            this.configuration = configuration;
            this.cameraManager.init({
                camera: {
                    minDistance: 1,
                    maxDistance: 10,
                    maxPolarAngle: 180,
                    position: { x: 0.2, y: 2, z: 2 },
                    lookAt: { x: 0, y: 0, z: 0 },
                    enablePan: true,
                },
            }, this.canvas);
            this.initScene();
            this.initLights();
            this.updateSizes();
            this.renderer.setSize(this.width, this.height);
            this.initHandlers();
            this.startRendering();
        });
    }
    destroy() {
        window.removeEventListener('resize', this.resizeHandler);
        window.removeEventListener('toggle-fullscreen', this.resizeHandler);
        this.control.removeEventListener('dragging-changed', this.handleTransformControlDragging);
        this.control.removeEventListener('change', this.handleTransformControlChange);
    }
    initLights() {
        const ambientLight = new AmbientLight('#ffffff', 0.3);
        this.scene.add(ambientLight);
    }
    startRendering() {
        requestAnimationFrame(() => {
            this.cameraManager.update();
            if (this.renderer) {
                this.renderer.render(this.scene, this.cameraManager.camera);
                this.startRendering();
            }
        });
    }
    initScene() {
        this.scene = new Scene();
        this.sceneNode = new Object3D();
        this.sceneNode.name = 'Scene';
        this.scene.background = new Color(0xeeeeee);
        const pMREMGenerator = new PMREMGenerator(this.renderer);
        pMREMGenerator.compileEquirectangularShader();
        new RGBELoader().setDataType(HalfFloatType).load(this.configuration.hdr, (texture) => {
            this.scene.environment = pMREMGenerator.fromEquirectangular(texture).texture;
            texture.dispose();
            pMREMGenerator.dispose();
        }, () => { }, (e) => console.error(e));
        this.scene.add(this.sceneNode);
        this.rootNode = new Object3D();
        this.rootNode.name = 'Root';
        this.sceneNode.add(this.rootNode);
        if (this.debug) {
            const axesHelper = new AxesHelper(1);
            this.scene.add(axesHelper);
        }
        this.loadView();
        this.createTransformControl();
    }
    initHandlers() {
        window.addEventListener('resize', this.resizeHandler);
        window.addEventListener('toggle-fullscreen', this.resizeHandler);
    }
    loadView() {
        return __awaiter(this, void 0, void 0, function* () {
            const model = yield this.modelLoader.load(this.configuration.model3d.url);
            this.rootNode.add(model);
            const glbConverter = new GlbConverter(this.width, this.height);
            const { position, lookAt } = glbConverter.getCameraParams(this.cameraManager.camera, this.rootNode, 'perspectiveRight');
            this.cameraManager.camera.position.copy(position);
            this.cameraManager.updateControlsTarget(lookAt);
        });
    }
    updateSizes() {
        const parent = this.canvas.parentElement;
        if (parent) {
            this.height = parent.clientHeight;
            this.width = parent.clientWidth;
        }
        else {
            console.error('No parent for canvas found');
        }
    }
    createTransformControl() {
        this.jointPointNode = new Object3D();
        this.jointPointNode.name = '';
        this.control = new TransformControls(this.cameraManager.camera, this.renderer.domElement);
        this.control.enabled = false;
        this.control.attach(this.jointPointNode);
        this.control.addEventListener('dragging-changed', this.handleTransformControlDragging);
        this.control.addEventListener('change', this.handleTransformControlChange);
        const gizmo = this.control.getHelper();
        this.rootNode.add(this.jointPointNode);
        this.scene.add(gizmo);
        this.control.getHelper().visible = false;
    }
}
function _getXYZ(vector) {
    return {
        x: getRoundedValue(vector.x),
        y: getRoundedValue(vector.y),
        z: getRoundedValue(vector.z),
    };
}
function getRoundedValue(value) {
    if (Math.abs(value) <= 1e-6) {
        return 0;
    }
    return Math.round(value * 1e3) / 1e3;
}
//# sourceMappingURL=JointManagerView3D.js.map